/**
 * Org Comparison Screen — Phase 5
 *
 * Route: /org-comparison?orgId=<uuid>
 *
 * School-vs-School (or org-vs-org) comparison of average team strength scores.
 * Shows the current org's rank within the top 10 and the full ranked list.
 *
 * Access: club roles only — owner / admin / coach.
 * No individual athlete data is shown — org aggregates only.
 *
 * Doctrine: DUEL_CLUB_MODE_ARCHITECTURE.md
 *   - No gym/home_gym_id dependency — org-membership scoped.
 *   - Aggregation via fetchOrgComparisonScores (lib/orgStrength.ts).
 */

import { useState, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  ScrollView,
  Pressable,
  SafeAreaView,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { useLocalSearchParams, useRouter, useFocusEffect } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { fetchOrganization, getOrgRoleForUser } from '@/lib/organizations';
import { resolveClubOwnerOrg } from '@/lib/orgProvisioning';
import { fetchOrgComparisonScores } from '@/lib/orgStrength';
import { TYPO } from '@/constants/typography';
import type { Organization, OrgRole, OrgType } from '@/types/database';
import type { OrgComparisonScore } from '@/lib/orgStrength';

// ─── Helpers ───────────────────────────────────────────────────────────────────

/** Format avg score as "412 kg" */
function fmtScore(score: number): string {
  return `${Math.round(score)} kg`;
}

/** Title varies by org type */
function screenTitle(orgType: OrgType | null): string {
  if (orgType === 'school') return 'School vs School';
  if (orgType === 'team') return 'Team Rankings';
  if (orgType === 'gym') return 'Gym Rankings';
  return 'Org Rankings';
}

// ─── Ranked Row ───────────────────────────────────────────────────────────────

function ComparisonRow({
  item,
  rank,
  isCurrentOrg,
  c,
}: {
  item: OrgComparisonScore;
  rank: number;
  isCurrentOrg: boolean;
  c: any;
}) {
  return (
    <View
      style={[
        rowStyles.row,
        {
          backgroundColor: isCurrentOrg ? c.accent + '14' : c.surface,
          borderColor: isCurrentOrg ? c.accent + '50' : c.border,
        },
      ]}
    >
      {/* Rank */}
      <Text style={[rowStyles.rankText, { color: isCurrentOrg ? c.accent : c.textTertiary }]}>
        #{rank}
      </Text>

      {/* Org name */}
      <View style={rowStyles.nameWrap}>
        <Text
          style={[rowStyles.orgName, { color: c.text }]}
          numberOfLines={1}
        >
          {item.orgName}
        </Text>
        {isCurrentOrg && (
          <Text style={[rowStyles.youLabel, { color: c.accent }]}>You</Text>
        )}
      </View>

      {/* Stats */}
      <View style={rowStyles.statsWrap}>
        <Text style={[rowStyles.scoreText, { color: c.text }]}>
          {fmtScore(item.averageStrengthScore)}
        </Text>
        <Text style={[rowStyles.countText, { color: c.textSecondary }]}>
          {item.memberCount} athletes
        </Text>
      </View>
    </View>
  );
}

const rowStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 6,
    borderWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    gap: 10,
  },
  rankText: {
    width: 36,
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    textAlign: 'center',
  },
  nameWrap: {
    flex: 1,
    gap: 2,
  },
  orgName: {
    fontSize: 15,
    fontFamily: 'Inter_600SemiBold',
  },
  youLabel: {
    fontSize: 11,
    fontFamily: 'Inter_600SemiBold',
  },
  statsWrap: {
    alignItems: 'flex-end',
    minWidth: 72,
  },
  scoreText: {
    fontSize: 15,
    fontFamily: 'Oswald_700Bold',
    textAlign: 'right',
  },
  countText: {
    fontSize: 11,
    fontFamily: 'Inter_400Regular',
    textAlign: 'right',
    marginTop: 1,
  },
});

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function OrgComparisonScreen() {
  const { orgId: paramOrgId } = useLocalSearchParams<{ orgId?: string }>();
  const router = useRouter();
  const { theme } = useTheme();
  const { session } = useAuth();
  const c = theme.colors;
  const userId = session?.user?.id ?? '';

  const [resolvedOrgId, setResolvedOrgId] = useState<string | null>(paramOrgId ?? null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [myRole, setMyRole] = useState<OrgRole | null>(null);
  const [comparisonData, setComparisonData] = useState<OrgComparisonScore[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);

    try {
      // Resolve orgId: prefer route param, fall back to owner org
      let orgId = paramOrgId ?? resolvedOrgId;
      if (!orgId) {
        orgId = await resolveClubOwnerOrg(userId);
        if (orgId) setResolvedOrgId(orgId);
      }

      if (!orgId) {
        setLoading(false);
        return;
      }

      const [fetchedOrg, role, scores] = await Promise.all([
        fetchOrganization(orgId),
        getOrgRoleForUser(orgId, userId),
        fetchOrgComparisonScores(),
      ]);

      setOrg(fetchedOrg);
      setMyRole(role);
      setComparisonData(scores);
    } finally {
      setLoading(false);
    }
  }, [userId, paramOrgId, resolvedOrgId]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const orgId = paramOrgId ?? resolvedOrgId;
  const isAdminOrOwner = myRole === 'owner' || myRole === 'admin';
  const isCoach = myRole === 'coach';
  const hasAccess = isAdminOrOwner || isCoach;

  const styles = makeStyles(c);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <SafeAreaView style={styles.centered}>
        <ActivityIndicator color={c.accent} />
      </SafeAreaView>
    );
  }

  // ── Access denied ──────────────────────────────────────────────────────────
  if (!hasAccess) {
    return (
      <SafeAreaView style={styles.centered}>
        <Text style={styles.heading}>Access Denied</Text>
        <Text style={styles.subText}>
          School comparison is available to owners, admins, and coaches.
        </Text>
        <Pressable style={styles.btnSecondary} onPress={() => router.back()}>
          <Text style={styles.btnSecondaryText}>Go Back</Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  // ── Derive current org position ────────────────────────────────────────────
  const orgType = (org?.org_type as OrgType) ?? null;
  const title = screenTitle(orgType);

  const currentOrgIndex = comparisonData.findIndex((s) => s.orgId === orgId);
  const currentOrgEntry = currentOrgIndex >= 0 ? comparisonData[currentOrgIndex] : null;
  const currentOrgRank = currentOrgIndex >= 0 ? currentOrgIndex + 1 : null;

  const hasData = comparisonData.length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }}>
      {/* ── Nav header ── */}
      <View style={styles.navRow}>
        <Pressable onPress={() => router.back()} style={styles.backBtn}>
          <Text style={styles.backBtnText}>← Back</Text>
        </Pressable>
        <Text style={styles.navTitle}>{title}</Text>
        <View style={{ width: 60 }} />
      </View>

      {/* Use ScrollView for header + list (FlatList for the ranked rows) */}
      <ScrollView contentContainerStyle={styles.content}>

        {/* ── SECTION A: Header ── */}
        <Text style={[styles.subtitle, { color: c.textSecondary }]}>
          Average team strength score across organizations
        </Text>

        {/* ── SECTION B: Your Org highlight ── */}
        <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>
          {orgType === 'school' ? 'Your School' : 'Your Organization'}
        </Text>
        <View
          style={[
            styles.yourOrgCard,
            {
              backgroundColor: c.accent + '10',
              borderColor: c.accent + '40',
            },
          ]}
        >
          <View style={styles.yourOrgTop}>
            <Text style={[styles.yourOrgName, { color: c.text }]} numberOfLines={1}>
              {org?.name ?? 'Your Org'}
            </Text>
            {currentOrgRank != null ? (
              <View style={[styles.rankBadge, { backgroundColor: c.accent + '20', borderColor: c.accent + '40' }]}>
                <Text style={[styles.rankBadgeText, { color: c.accent }]}>
                  #{currentOrgRank}
                </Text>
              </View>
            ) : (
              <View style={[styles.rankBadge, { backgroundColor: c.surfaceVariant ?? c.surface, borderColor: c.border }]}>
                <Text style={[styles.rankBadgeText, { color: c.textSecondary }]}>
                  Not yet ranked
                </Text>
              </View>
            )}
          </View>

          {currentOrgEntry ? (
            <View style={styles.yourOrgStats}>
              <View style={styles.yourOrgStat}>
                <Text style={[styles.yourOrgStatValue, { color: c.text }]}>
                  {fmtScore(currentOrgEntry.averageStrengthScore)}
                </Text>
                <Text style={[styles.yourOrgStatLabel, { color: c.textSecondary }]}>
                  avg strength
                </Text>
              </View>
              <View style={[styles.statDivider, { backgroundColor: c.border }]} />
              <View style={styles.yourOrgStat}>
                <Text style={[styles.yourOrgStatValue, { color: c.text }]}>
                  {currentOrgEntry.memberCount}
                </Text>
                <Text style={[styles.yourOrgStatLabel, { color: c.textSecondary }]}>
                  Based on {currentOrgEntry.memberCount} athlete{currentOrgEntry.memberCount !== 1 ? 's' : ''}
                </Text>
              </View>
            </View>
          ) : (
            <Text style={[styles.notRankedText, { color: c.textSecondary }]}>
              — No eligible athletes yet. Members must complete onboarding with a public profile and strength score.
            </Text>
          )}
        </View>

        {/* ── SECTION C / D: Ranked list or empty state ── */}
        {!hasData ? (
          /* SECTION D — Empty state */
          <View style={[styles.emptyCard, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.emptyEmoji]}>🏫</Text>
            <Text style={[styles.emptyTitle, { color: c.text }]}>
              No comparisons yet
            </Text>
            <Text style={[styles.emptyText, { color: c.textSecondary }]}>
              As more schools join DUEL, you'll be able to compare team strength here.
            </Text>
          </View>
        ) : (
          <>
            {/* SECTION C — Ranked list header */}
            <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>
              Rankings
            </Text>
            <View style={[styles.colHeader, { borderBottomColor: c.border }]}>
              <Text style={[styles.colRank, { color: c.textTertiary }]}>Rank</Text>
              <Text style={[styles.colName, { color: c.textTertiary }]}>Organization</Text>
              <Text style={[styles.colScore, { color: c.textTertiary }]}>Avg Score</Text>
            </View>

            {/* Render inline (ScrollView + map — avoids nested FlatList issues) */}
            <View style={styles.listWrap}>
              {comparisonData.map((item, idx) => (
                <ComparisonRow
                  key={item.orgId}
                  item={item}
                  rank={idx + 1}
                  isCurrentOrg={item.orgId === orgId}
                  c={c}
                />
              ))}
            </View>

            <Text style={[styles.footerNote, { color: c.textTertiary }]}>
              Top {comparisonData.length} organizations by average strength score · Eligible: active members with public profiles
            </Text>
          </>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function makeStyles(c: any) {
  return StyleSheet.create({
    centered: {
      flex: 1,
      backgroundColor: c.background,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 32,
    },
    heading: {
      fontSize: 22,
      fontFamily: TYPO.heading.bold,
      color: c.text,
      textAlign: 'center',
      marginBottom: 12,
    },
    subText: {
      fontSize: 15,
      color: c.textSecondary,
      textAlign: 'center',
      lineHeight: 22,
      marginBottom: 24,
    },
    btnSecondary: {
      borderRadius: 6,
      borderWidth: 1.5,
      borderColor: c.border,
      paddingVertical: 12,
      paddingHorizontal: 24,
      alignItems: 'center',
    },
    btnSecondaryText: {
      color: c.text,
      fontFamily: TYPO.body.regular,
      fontSize: 15,
    },
    navRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    backBtn: { width: 60 },
    backBtnText: {
      color: c.accent,
      fontSize: 15,
      fontFamily: TYPO.body.regular,
    },
    navTitle: {
      fontSize: 17,
      fontFamily: TYPO.heading.bold,
      color: c.text,
      textAlign: 'center',
    },
    content: {
      padding: 16,
      paddingBottom: 32,
    },
    subtitle: {
      fontSize: 14,
      fontFamily: TYPO.body.regular,
      marginBottom: 20,
      lineHeight: 20,
    },
    sectionLabel: {
      fontSize: 12,
      fontFamily: TYPO.body.semibold,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      marginBottom: 10,
    },
    // Section B — Your org card
    yourOrgCard: {
      borderRadius: 8,
      borderWidth: 1.5,
      padding: 16,
      marginBottom: 24,
    },
    yourOrgTop: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 12,
      gap: 8,
    },
    yourOrgName: {
      fontSize: 18,
      fontFamily: TYPO.heading.bold,
      flex: 1,
    },
    rankBadge: {
      borderRadius: 20,
      borderWidth: 1,
      paddingHorizontal: 10,
      paddingVertical: 4,
    },
    rankBadgeText: {
      fontSize: 13,
      fontFamily: TYPO.heading.bold,
    },
    yourOrgStats: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
    yourOrgStat: {
      alignItems: 'flex-start',
    },
    yourOrgStatValue: {
      fontSize: 22,
      fontFamily: TYPO.heading.bold,
      lineHeight: 28,
    },
    yourOrgStatLabel: {
      fontSize: 11,
      fontFamily: TYPO.body.semibold,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginTop: 2,
    },
    statDivider: {
      width: 1,
      height: 28,
    },
    notRankedText: {
      fontSize: 13,
      fontFamily: TYPO.body.regular,
      lineHeight: 18,
    },
    // Column headers
    colHeader: {
      flexDirection: 'row',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderBottomWidth: 1,
      marginBottom: 8,
    },
    colRank: {
      width: 46,
      fontSize: 11,
      fontFamily: TYPO.body.semibold,
      textTransform: 'uppercase',
    },
    colName: {
      flex: 1,
      fontSize: 11,
      fontFamily: TYPO.body.semibold,
      textTransform: 'uppercase',
    },
    colScore: {
      width: 80,
      fontSize: 11,
      fontFamily: TYPO.body.semibold,
      textTransform: 'uppercase',
      textAlign: 'right',
    },
    listWrap: {
      gap: 6,
      marginBottom: 16,
    },
    footerNote: {
      fontSize: 11,
      fontFamily: TYPO.body.regular,
      textAlign: 'center',
      lineHeight: 16,
      marginTop: 8,
    },
    // Section D — Empty state
    emptyCard: {
      borderRadius: 8,
      borderWidth: 1,
      padding: 24,
      alignItems: 'center',
      marginTop: 8,
    },
    emptyEmoji: {
      fontSize: 40,
      marginBottom: 12,
    },
    emptyTitle: {
      fontSize: 17,
      fontFamily: TYPO.heading.bold,
      marginBottom: 8,
      textAlign: 'center',
    },
    emptyText: {
      fontSize: 14,
      fontFamily: TYPO.body.regular,
      lineHeight: 20,
      textAlign: 'center',
    },
  });
}
